<script lang="ts">
	//Formatters
	import { format } from 'svelte-i18n';
	//Helpers
	import { onSettingsResponse } from '../../../helpers/device_settings_helpers';
	import { input_settings_check, is1LWithMultipleInputs } from '../../../helpers/interface_helpers';
	import { bulkUpdate } from './app_settings_helper';
	//Stores
	import { readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { devices } from 'src/stores/lists/devices';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	//Design system components
	import { Alert, Toggle, ToggleV2 } from '@Shelly/shelly-design-system';
	//Types
	import type { Device } from '../../../types/Device';
	import type { InputClickEvent } from 'src/types/InputClickEvent';
	import SettingGuide from '../SettingGuide.svelte';
	import InputStatePreview from './InputStatePreview.svelte';

	export let device: Device;
	export let title = '';
	export let deferSave = false;
	export let value: boolean | undefined

	const id = device.id;
	const settings = readDeviceSettings(id);
	const btnTypeNotMomentary = settings && input_settings_check(settings, device);
	const is1L = is1LWithMultipleInputs(device?.type);

	let checked = $devices[id]?.input_state || false;
	let waitForResponse = false;
	let showPreview = false;

	$: displayed = deferSave && typeof value === 'boolean' ? value : checked;
	$: ToggleC = $showNewLayout ? ToggleV2 : Toggle;

	const save = async (e: InputClickEvent) => {
		if (deferSave) {
			value = !(typeof value === 'boolean' ? value : checked);
			return;
		}
		waitForResponse = true;
		checked = e.target.checked;
		const input_state = checked;
		const res = await bulkUpdate(id, { input_state });

		if (!res?.isok) {
			// Revert to prev value when request fails
			checked = !checked;
		}

		onSettingsResponse(res, device);
		waitForResponse = false;
	};
</script>

<SettingGuide
	{title}
	text={is1L
		? $format('settings_guide_input_state_1l', {
				default:
					'Show whether each physical input is on or off as status labels on the device card.',
			})
		: $format('settings_guide_input_state', {
				default:
					'Show whether the physical switch or button is on or off. On the device page and classic list it is a color bar on the power button. On compact list and dashboard cards it is an Input On/Off chip.',
			})}
	linkText={$format('settings_link_input_state_preview', { default: 'Preview how this looks' })}
	on:click={() => {
		showPreview = true;
	}}
/>

<svelte:component
	this={ToggleC}
	size="m"
	checked={displayed}
	disabled={waitForResponse}
	class="mb-s"
	on:click={save}
	id="toggle-6pq4c86"
>
	{is1L
		? $format('input_state_identifier_new', {
				default: 'Add input state identifiers to the device status labels.',
			})
		: $format('input_state_identifier', {
				default: 'Show physical input on the power button and as a chip on device cards.',
			})}
</svelte:component>

{#if settings && !btnTypeNotMomentary && !is1L}
	<Alert
		heading={$format('input_state_hidden_warning', {
			default:
				'"Input state" identifier is hidden because the channel button type is set to "Momentary".',
		})}
		type={'warning'}
	/>
{/if}

<InputStatePreview bind:showModal={showPreview} {is1L} />
